import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Ban, FileDown, Send } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { paraElServidor } from "@/lib/decimales"
import { hoyLocal } from "@/lib/fechas"

import { useCatalogo } from "./Catalogo"
import { Documentos } from "./Documentos"
import { cifra, descargarRecibo } from "./descarga"
import { LINEA_VACIA, LineasRecibo } from "./LineasRecibo"
import {
  type Asignacion,
  type Chofer,
  type Empresa,
  ESTADOS,
  type Linea,
  MONEDAS,
  numero,
  type Recibo,
  TONO_ESTADO,
  type Vehiculo,
} from "./tipos"

type Datos = Omit<
  Recibo,
  | "id"
  | "intermediario_id"
  | "numero"
  | "estado"
  | "total"
  | "emitido_en"
  | "recibido_en"
  | "anulado_en"
  | "motivo_anulacion"
  | "creado_en"
  | "snapshot"
>

const vacio = (asig?: Asignacion): Datos => ({
  asignacion_id: asig?.id ?? null,
  empresa_id: null,
  vehiculo_id: null,
  chofer_id: null,
  fecha: hoyLocal(),
  moneda: "BS",
  expediente_numero: asig?.numero ?? null,
  bl: asig?.carga.bl ?? null,
  origen: "Puerto Cabello",
  destino: null,
  condiciones_pago: "Contado",
  observaciones: null,
  // Desde una carga asignada, un renglón por flete con su concepto y su cantidad: el
  // precio lo pone el intermediario.
  lineas: asig?.fletes.length
    ? asig.fletes.map((f) => ({
        ...LINEA_VACIA,
        descripcion: f.concepto ?? LINEA_VACIA.descripcion,
        cantidad: String(Math.max(1, Number(f.cantidad) || 1)),
      }))
    : [LINEA_VACIA],
})

const mensaje = (e: unknown) => (e instanceof ErrorApi ? e.message : "No se pudo completar")

/** El recibo de un flete: se llena en borrador, se emite (número y foto) y se imprime. */
export function EditorRecibo({
  recibo,
  asignacion,
  onCerrar,
}: {
  recibo: Recibo | null
  /** La carga asignada desde el 2.0, al hacer un recibo nuevo de ella. */
  asignacion?: Asignacion
  onCerrar: () => void
}) {
  const cliente = useQueryClient()
  const [actual, setActual] = useState<Recibo | null>(recibo)
  const [d, setD] = useState<Datos>(() =>
    recibo
      ? { ...recibo, lineas: recibo.lineas.length ? recibo.lineas : [LINEA_VACIA] }
      : vacio(asignacion),
  )
  const [motivo, setMotivo] = useState("")
  const empresas = useCatalogo<Empresa>("EMPRESA")
  const vehiculos = useCatalogo<Vehiculo>("VEHICULO")
  const choferes = useCatalogo<Chofer>("CHOFER")
  const editable = !actual || actual.estado === "BORRADOR"

  const refrescar = (r: Recibo | null) => {
    setActual(r)
    // También las cargas asignadas: su cuenta de recibos cambia al emitir.
    cliente.invalidateQueries({ queryKey: ["transporte", "recibos"] })
    cliente.invalidateQueries({ queryKey: ["transporte", "asignaciones"] })
  }
  const cuerpo = () => ({
    ...d,
    expediente_numero: d.expediente_numero?.trim() || null,
    lineas: d.lineas.map((l: Linea) => ({
      ...l,
      cantidad: paraElServidor(l.cantidad),
      precio_unitario: paraElServidor(l.precio_unitario || "0"),
      contenedor: l.contenedor?.trim() || null,
    })),
  })
  const guardar = async () => {
    const r = actual
      ? await api.put<Recibo>(`/api/v1/transporte/recibos/${actual.id}`, cuerpo())
      : await api.post<Recibo>("/api/v1/transporte/recibos", cuerpo())
    refrescar(r)
    return r
  }
  const borrador = useMutation({ mutationFn: guardar })
  const emitir = useMutation({
    mutationFn: async () => {
      const r = await guardar()
      return api.post<Recibo>(`/api/v1/transporte/recibos/${r.id}/emitir`)
    },
    onSuccess: refrescar,
  })
  const anular = useMutation({
    mutationFn: () =>
      api.post<Recibo | null>(`/api/v1/transporte/recibos/${actual?.id}/anular`, { motivo }),
    onSuccess: (r) => {
      refrescar(r)
      if (!r) onCerrar()
    },
  })
  const pdf = useMutation({ mutationFn: () => descargarRecibo(actual as Recibo) })
  const error = borrador.error ?? emitir.error ?? anular.error ?? pdf.error

  const sel = (clave: "empresa_id" | "vehiculo_id" | "chofer_id", valor: string) =>
    setD({
      ...d,
      [clave]: valor ? Number(valor) : null,
      ...(clave === "empresa_id" ? { vehiculo_id: null } : {}),
    })
  const texto = (clave: keyof Datos, etiqueta: string, ancho = "") => (
    <label className={`grid gap-1 text-sm ${ancho}`}>
      {etiqueta}
      <input
        className="campo"
        // El expediente y el BL de una carga asignada son los de SETIECA, no se cambian.
        disabled={
          !editable || (!!d.asignacion_id && (clave === "bl" || clave === "expediente_numero"))
        }
        type={clave === "fecha" ? "date" : "text"}
        value={(d[clave] as string | null) ?? ""}
        onChange={(e) => setD({ ...d, [clave]: e.target.value || null })}
      />
    </label>
  )
  const delaEmpresa = (vehiculos.data ?? []).filter(
    (v) => v.empresa_id === d.empresa_id && v.activo,
  )
  const titulo = actual?.numero ? `Recibo Nº ${numero(actual.numero)}` : "Nuevo recibo de flete"

  return (
    <Modal abierto={true} titulo={titulo} onCerrar={onCerrar} ancho="lg">
      {actual && (
        <p className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <Insignia tono={TONO_ESTADO[actual.estado]}>{ESTADOS[actual.estado]}</Insignia>
          {actual.total && (
            <span className="tabular-nums">
              Total {cifra(actual.total)} {MONEDAS[actual.moneda]}
            </span>
          )}
          {actual.motivo_anulacion && (
            <span style={{ color: "var(--fg-muted)" }}>Motivo: {actual.motivo_anulacion}</span>
          )}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="grid gap-1 text-sm sm:col-span-3">
          Empresa de transporte que hizo el flete *
          <select
            className="campo"
            disabled={!editable}
            value={d.empresa_id ?? ""}
            onChange={(e) => sel("empresa_id", e.target.value)}
          >
            <option value="">Elija…</option>
            {(empresas.data ?? [])
              .filter((e) => e.activo || e.id === d.empresa_id)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.razon_social} · {e.rif}
                </option>
              ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Vehículo *
          <select
            className="campo"
            disabled={!editable || !d.empresa_id}
            value={d.vehiculo_id ?? ""}
            onChange={(e) => sel("vehiculo_id", e.target.value)}
          >
            <option value="">{d.empresa_id ? "Elija…" : "Primero la empresa"}</option>
            {delaEmpresa.map((v) => (
              <option key={v.id} value={v.id}>
                {v.placa}
                {v.placa_remolque ? ` / ${v.placa_remolque}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm sm:col-span-2">
          Chofer *
          <select
            className="campo"
            disabled={!editable}
            value={d.chofer_id ?? ""}
            onChange={(e) => sel("chofer_id", e.target.value)}
          >
            <option value="">Elija…</option>
            {(choferes.data ?? [])
              .filter((c) => c.activo || c.id === d.chofer_id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombres} {c.apellidos} · C.I. {c.cedula}
                </option>
              ))}
          </select>
        </label>
        {texto("fecha", "Fecha del servicio")}
        <label className="grid gap-1 text-sm">
          Moneda
          <select
            className="campo"
            disabled={!editable}
            value={d.moneda}
            onChange={(e) => setD({ ...d, moneda: e.target.value as Datos["moneda"] })}
          >
            <option value="BS">Bolívares</option>
            <option value="USD">Dólares</option>
            <option value="EUR">Euros</option>
          </select>
        </label>
        {texto("condiciones_pago", "Condiciones de pago")}
        {texto("bl", "BL")}
        {texto(
          "expediente_numero",
          d.asignacion_id
            ? "Expediente de SETIECA (carga asignada)"
            : "Nº de expediente de SETIECA (opcional)",
          "sm:col-span-2",
        )}
        {texto("origen", "Origen")}
        {texto("destino", "Destino", "sm:col-span-2")}
        {texto("observaciones", "Observaciones", "sm:col-span-3")}
      </div>
      <h3 className="mb-2 mt-5 text-sm font-semibold">Renglones</h3>
      {editable ? (
        <LineasRecibo
          lineas={d.lineas}
          onCambiar={(lineas) => setD({ ...d, lineas })}
          moneda={MONEDAS[d.moneda]}
        />
      ) : (
        <ul className="space-y-1 text-sm">
          {(actual?.snapshot?.lineas ?? []).map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: renglones congelados, sin id
            <li key={i} className="flex justify-between gap-2 tabular-nums">
              <span>
                {l.cantidad} × {l.descripcion} {l.contenedor ? `· ${l.contenedor}` : ""}
              </span>
              <span>{cifra(l.importe)}</span>
            </li>
          ))}
        </ul>
      )}
      <section className="mt-5">
        <h3 className="mb-2 text-sm font-semibold">Soportes del servicio</h3>
        {actual ? (
          <Documentos dueno="RECIBO" duenoId={actual.id} soloLectura={!editable} />
        ) : (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Guarde el borrador para subir la guía o el soporte de entrega.
          </p>
        )}
      </section>
      {error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {mensaje(error)}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {actual?.estado === "EMITIDO" && (
          <>
            <input
              className="campo mr-auto min-w-0 flex-1"
              placeholder="Motivo para anular"
              aria-label="Motivo para anular"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
            <Boton
              variante="peligro"
              disabled={motivo.trim().length < 5 || anular.isPending}
              onClick={() => anular.mutate()}
            >
              <Ban size={16} aria-hidden={true} /> Anular
            </Boton>
          </>
        )}
        {actual?.estado === "BORRADOR" && (
          <Boton variante="fantasma" disabled={anular.isPending} onClick={() => anular.mutate()}>
            Descartar borrador
          </Boton>
        )}
        {editable && (
          <>
            <Boton disabled={borrador.isPending} onClick={() => borrador.mutate()}>
              Guardar borrador
            </Boton>
            <Boton variante="primario" disabled={emitir.isPending} onClick={() => emitir.mutate()}>
              <Send size={16} aria-hidden={true} /> Emitir recibo
            </Boton>
          </>
        )}
        {actual?.snapshot && (
          <Boton variante="primario" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
            <FileDown size={16} aria-hidden={true} /> Descargar PDF
          </Boton>
        )}
      </div>
    </Modal>
  )
}
